import { useCallback, useMemo, useState } from 'react';
import { Button, Card, CreateNewCardHolder, Modal } from '@/components/generic';
import { useTranslation } from 'react-i18next';
import { useGetCardHolderData } from '@package/smart-wallet/hooks';
import { useCardContext } from '../context/useCardContext';
import ActivateCardModal from './ActivateCardModal';
import RegisterPhysicalCardModal from './RegisterPhysicalCardModal';
import { TCardHolder } from '@xhype/card-app-sdk';

const physicalCardTypeId = Number(import.meta.env.VITE_PHYSICAL_CARD_TYPE_ID);


const PhysicalCard = () => {
  const { t } = useTranslation();
  const { refetch, selectedCardInfo } = useCardContext();
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isCreateCardHolderModalOpen, setIsCreateCardHolderModalOpen] = useState(false);

  const { cardHolderInfo, isLoading: isLoadingCardHolder } = useGetCardHolderData();

  const physicalCardHolder = useMemo(() => {
    if (!cardHolderInfo?.records) return null;
    return cardHolderInfo.records.find(
      (record: TCardHolder) => record.cardTypeId === physicalCardTypeId,
    ) ?? null;
  }, [cardHolderInfo]);

  const canActivateCard = useMemo(() => {
    return (
      selectedCardInfo?.cardTypeId === physicalCardTypeId &&
      selectedCardInfo?.status === 'un_activated'
    );
  }, [selectedCardInfo?.cardTypeId, selectedCardInfo?.status]);

  const handleRegisterSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleActivateSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  if (!import.meta.env.VITE_PHYSICAL_CARD_TYPE_ID) return null;

  return (
    <>
      <Card classHead='!pl-3' title={t('cards.physical_card.title')}>
        <div className='px-2 pb-3'>
          <p className='mb-4 text-body-2 text-t-secondary'>
            {t('cards.physical_card.subtitle')}
          </p>

          {!isLoadingCardHolder && physicalCardHolder && physicalCardHolder.status !== 'pass_audit' && (
            <div className='mb-4 flex items-center gap-2 text-body-2'>
              <span className='text-t-secondary'>
                {t('cards.physical_card.cardholder_status')}:
              </span>
              <span
                className={`font-medium ${
                  physicalCardHolder.status === 'reject'
                    ? 'text-red-500'
                    : 'text-yellow-500'
                }`}
              >
                {t(`cards.physical_card.status.${physicalCardHolder.status}`)}
              </span>
            </div>
          )}

          <div className='flex gap-3'>
            <Button
              className='flex-1 h-10!'
              isStroke
              type='button'
              disabled={!!physicalCardHolder && physicalCardHolder.status !== 'pass_audit'}
              onClick={() =>
                !physicalCardHolder
                  ? setIsCreateCardHolderModalOpen(true)
                  : setIsRegisterModalOpen(true)
              }
            >
              {t('cards.physical_card.register')}
            </Button>
            {canActivateCard && (
              <Button
                className='flex-1 h-10!'
                isBlack
                type='button'
                onClick={() => setIsActivateModalOpen(true)}
              >
                {t('cards.physical_card.activate')}
              </Button>
            )}
          </div>
        </div>
      </Card>

      <RegisterPhysicalCardModal
        open={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSuccess={handleRegisterSuccess}
      />

      {canActivateCard && selectedCardInfo?.cardNo && (
        <ActivateCardModal
          open={isActivateModalOpen}
          onClose={() => setIsActivateModalOpen(false)}
          onSuccess={handleActivateSuccess}
          cardNo={selectedCardInfo.cardNo}
        />
      )}

      <Modal
        open={isCreateCardHolderModalOpen}
        onClose={() => setIsCreateCardHolderModalOpen(false)}
      >
        <CreateNewCardHolder
          onClose={() => setIsCreateCardHolderModalOpen(false)}
          cardTypeData={{ cardTypeId: physicalCardTypeId }}
        />
      </Modal>
    </>
  );
};

export default PhysicalCard;
